import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Check, Gift, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

const TIERS = [
  { range: "Par véhicule régulé (tous types)", price: "100 € / mois" },
  { range: "Par site de départ-arrivée supplémentaire", price: "+200 € / mois" },
];

export default function Billing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [polling, setPolling] = useState(false);
  const [vehicles, setVehicles] = useState(0);
  const [extraSites, setExtraSites] = useState(0);

  const loadDash = useCallback(() => api.get("/dashboard").then((r) => {
    setData(r.data);
    setVehicles(r.data.vehicles || 0);
    setExtraSites(r.data.tier?.extra_sites || 0);
  }), []);
  useEffect(() => { loadDash(); }, [loadDash]);

  const total = vehicles * 100 + extraSites * 200;

  const poll = useCallback(async (pid, attempt = 0) => {
    if (attempt > 8) { setPolling(false); toast.error("Délai de vérification dépassé."); return; }
    try {
      const { data: st } = await api.get(`/billing/status/${pid}`);
      if (st.payment_status === "paid") {
        setPolling(false);
        toast.success("Paiement confirmé — abonnement activé !");
        loadDash();
        return;
      }
      if (["expired", "canceled", "failed"].includes(st.payment_status)) {
        setPolling(false);
        toast.error(`Paiement ${st.payment_status === "canceled" ? "annulé" : st.payment_status === "failed" ? "échoué" : "expiré"}.`);
        return;
      }
      setTimeout(() => poll(pid, attempt + 1), 2000);
    } catch (e) { setPolling(false); toast.error(apiError(e)); }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pid = params.get("payment_id");
    if (pid) {
      setPolling(true);
      poll(pid);
      params.delete("payment_id");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        vehicles: Number(vehicles),
        extra_sites: Number(extraSites),
      });
      window.location.href = res.url;
    } catch (err) { toast.error(apiError(err)); setLoading(false); }
  };

  if (!data) return <p className="text-muted-foreground">Chargement…</p>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Abonnement</h1>
        <p className="text-muted-foreground text-sm">Tarification à 100 € / mois par véhicule régulé. Zéro commission sur les échanges.</p>
      </div>

      {polling && (
        <Card className="p-4 rounded-md shadow-none flex items-center gap-3 border-primary" data-testid="billing-polling">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm">Vérification du paiement en cours…</span>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-6 rounded-md shadow-none border-primary/40">
          <Badge variant="outline" className={data.subscription_status === "active" ? "border-green-500 text-green-600" : "border-accent bg-accent/20 text-amber-700 dark:text-amber-300"}>
            {data.subscription_status === "active" ? "Actif" : "Essai gratuit"}
          </Badge>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs">Véhicules à facturer</Label>
              <Input type="number" min={0} max={500} value={vehicles}
                onChange={(e) => setVehicles(Math.max(0, Number(e.target.value) || 0))}
                className="mt-1" data-testid="billing-vehicles" />
              <p className="text-[11px] text-muted-foreground mt-1">{data.vehicles} actif(s) · 100 €/véhicule</p>
            </div>
            <div>
              <Label className="text-xs">Sites suppl.</Label>
              <Input type="number" min={0} max={50} value={extraSites}
                onChange={(e) => setExtraSites(Math.max(0, Number(e.target.value) || 0))}
                className="mt-1" data-testid="billing-extra-sites" />
              <p className="text-[11px] text-muted-foreground mt-1">départ/arrivée · 200 €/site</p>
            </div>
          </div>

          <div className="flex items-end gap-2 mt-4">
            <span className="font-display text-4xl font-extrabold" data-testid="billing-total">{total.toLocaleString("fr-FR")} €</span>
            <span className="text-muted-foreground mb-1.5">/ mois</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {vehicles} × 100 €{extraSites > 0 ? ` + ${extraSites} × 200 €` : ""}
          </p>

          <Button onClick={checkout} disabled={loading || total <= 0} className="mt-5 w-full gap-2" data-testid="billing-checkout">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {data.subscription_status === "active" ? "Renouveler maintenant" : "Payer avec Mollie"}
          </Button>
        </Card>

        <Card className="p-6 rounded-md shadow-none">
          <div className="flex items-center gap-2 mb-3"><Gift className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-semibold">Mois gratuits</h2></div>
          <p className="font-display text-4xl font-extrabold" data-testid="billing-free-months">{data.free_months_balance}</p>
          <p className="text-sm text-muted-foreground mt-1">mois offerts cumulés via parrainage</p>
          <div className="mt-4 bg-secondary rounded-md p-3">
            <p className="text-xs text-muted-foreground">Votre code parrainage</p>
            <code className="font-mono font-semibold">{data.referral_code}</code>
            <p className="text-xs text-muted-foreground mt-1">{data.referrals_count} filleul(s) · +1 mois par 1er paiement filleul</p>
          </div>
        </Card>
      </div>

      <Card className="p-6 rounded-md shadow-none">
        <h2 className="font-display text-lg font-semibold mb-4">Grille tarifaire</h2>
        <div className="space-y-2">
          {TIERS.map((t) => (
            <div key={t.range} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm">
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{t.range}</span>
              <span className="font-semibold">{t.price}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4">1 mois d'essai offert à la création, sans engagement. Aucune commission sur la bourse d'échange. Paiement sécurisé par Mollie.</p>
      </Card>
    </div>
  );
}
